import { useEffect, useMemo, useState } from "react";
import { Box, Typography, IconButton, useTheme, useMediaQuery } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { Images } from "../../../utils/Images";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";
import { useLocation } from "react-router-dom";

const Faq = () => {
  const [openItems, setOpenItems] = useState([0, 1]); // First two items open by default
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // <600px
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");
  const isBelow900 = useMediaQuery("(max-width: 899px)");

  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const location = useLocation();

  // Ensure CMS home data is loaded
  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);

  // Build FAQ items from CMS
  const faqData = useMemo(() => {
    const sections = Array.isArray(cmsHome?.data) ? cmsHome.data : [];

    // Choose FAQ section based on current route:
    // - /story/...               -> medical FAQ (id: 78)
    // - /monthly-donation-detail -> monthly donation FAQ (id: 79)
    // - /life or /life/:slug     -> life donation FAQ (id: 107)
    // - default                  -> normal campaign FAQ (id: 77)
    const path = (location.pathname || "").toLowerCase();
    let targetId = 77;
    if (path.startsWith("/story/")) {
      targetId = 78;
    } else if (path.startsWith("/monthly-donation-detail")) {
      targetId = 79;
    } else if (path.startsWith("/life")) {
      targetId = 107;
    }

    const faqSection = sections.find((s) => s?.id === targetId);

    const related = Array.isArray(faqSection?.relatedCMS)
      ? faqSection.relatedCMS.filter((item) => item.status === 1)
      : [];

    // Map CMS related items into question/answer pairs
    const items = related.map((item) => ({
      question: item.title || "",
      // description comes as HTML; strip basic tags for now
      answer: item.description
        ? item.description.replace(/<[^>]+>/g, "").trim()
        : "",
    }));

    // Fallback to empty array if nothing configured
    return items.length > 0 ? items : [];
  }, [cmsHome, location.pathname]);

  const isLifePage = (location.pathname || "").toLowerCase().startsWith("/life");

  const toggleItem = (index) => {
    setOpenItems((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index]
    );
  };

  return (
    <Box
      sx={{
        // maxWidth: "94.44%",
        width: "100%",
        // mx: "auto",
      }}
    >
      <Box
        sx={{
          // maxWidth: "1360px",
          width: "100%",
          mx: "auto",
          p: { xs: 0, md: 6 },
          display: "flex",
          flexDirection: "column",
          alignItems: isMobile ? "start" : isAbove1200 ? "center" : "center",
          background: "#FFFFFF",
          borderRadius: "20px",
          border: isMobile
            ? 'none'
            : isAbove1200
              ? '1.02px solid #EAEAF0'
              : '1.02px solid #EAEAF0',
        }}
      >
        {/* Title */}
        <Typography
          variant="h4"
          align="center"
          sx={{
            mb: isMobile ? 3 : 4,
            color: "#000000",
            fontSize: isMobile ? "14px" : isAbove1200 ? "24px" : "18px",
            fontWeight: 700,
          }}
        >
          {isLifePage ? "Medical Frequently Asked Questions" : "Frequently Asked Questions"}
        </Typography>

        {/* FAQ List */}
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}
        >
          {faqData.map((item, index) => (
            <Box
              key={index}
              sx={{
                bgcolor: "#fff",
                borderRadius: 3,
                boxShadow: "0px 5px 16px 0px #080F340F",
                border: "1px solid #FFFFFF",
                p: isMobile ? 2 : 3,
                transition: "box-shadow 0.2s",
              }}
            >
              {/* Question Row */}
              <Box
                onClick={() => toggleItem(index)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  pb: openItems.includes(index) ? 1 : 0,
                  gap: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "#1D1D1B",
                    fontSize: isMobile ? "12px" : "18px",
                    fontWeight: 600,
                    pr: 1,
                  }}
                >
                  {item.question}
                </Typography>
                <IconButton
                  disableRipple
                  sx={{
                    width: 26,
                    height: 26,
                    bgcolor: openItems.includes(index) ? "#29CC6A" : "#F5F8ED",
                    borderRadius: "3px",
                    border: openItems.includes(index)
                      ? "none"
                      : "1.5px solid #F3F3F3",
                    boxShadow: openItems.includes(index)
                      ? "0px 2px 8px 0px #22C55E33"
                      : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 0,
                    transition: "all 0.2s",
                  }}
                >
                  <img
                    src={openItems.includes(index) ? Images.minusIcon : Images.plusIcon}
                    alt={openItems.includes(index) ? "Collapse" : "Expand"}
                    style={{ width: 11, height: 11 }}
                  />
                </IconButton>
              </Box>

              {/* Answer */}
              {openItems.includes(index) && (
                <Typography
                  sx={{
                    color: "#6F6C90",
                    fontSize: isBelow900 ? "12px" : "16px",
                    fontWeight: 400,
                    mt: isMobile ? 0.5 : 1.5,
                    lineHeight: 1.6,
                  }}
                >
                  {item.answer}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Faq;
